"use client";
import { countryOf } from "@commerce-frontend/i18n/helpers";
import { Link, usePathname } from "@commerce-frontend/i18n/navigation";
import type { Locale } from "@commerce-frontend/i18n/types";
import type { ImageAssetFragment } from "@commerce-frontend/types";
import { Icon } from "@commerce-frontend/ui/base/Icon/Icon";
import { cn } from "@commerce-frontend/ui/helpers/styles";
import { Button } from "@commerce-frontend/ui/primitives/Button";
import * as Dialog from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useLocale, useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { Configure, Index, useInstantSearch, useSearchBox } from "react-instantsearch";
import { AssetImage } from "~/components/AssetImage/AssetImage";
import { useEnvVar } from "~/lib/config/context";
import type { StoreMetadata } from "~/lib/store-config/context";
import { useMenu } from "../../MenuContext";
import { SearchBar } from "../SearchBar/SearchBar";
import { SearchProducts } from "./SearchProducts/SearchProducts";
import { SearchStores } from "./SearchStores/SearchStores";

type Props = {
	logo?: ImageAssetFragment;
	isScrolled: boolean;
} & Pick<StoreMetadata, "siteName">;

export const SearchModal = ({ logo, siteName, isScrolled }: Props) => {
	const { searchOpen, setSearchOpen, searchQuery } = useMenu();
	const { query } = useSearchBox();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const { results, status, renderState } = useInstantSearch();
	const locale = useLocale() as Locale;
	const country = countryOf(locale).toLowerCase();
	const t = useTranslations("SearchModal");
	const tSearchBar = useTranslations("SearchBar");
	const [animationClass, setAnimationClass] = useState("translate-y-24");
	const indexName = useEnvVar("NEXT_PUBLIC_ALGOLIA_STORES_INDEX") ?? "";

	useEffect(() => {
		setSearchOpen(false);
	}, [pathname, searchParams]);

	const noResults = Object.keys(renderState).every((indexName) => {
		const results = renderState[indexName];
		return results && results?.hits?.items?.length === 0;
	});

	// Run the animation only after the modal is open
	useEffect(() => {
		setTimeout(() => {
			setAnimationClass(searchOpen ? "translate-y-0" : "translate-y-24");
		}, 100);
	}, [searchOpen]);

	return (
		<Dialog.Root open={searchOpen} onOpenChange={setSearchOpen}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-15 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 z-[100]" />
				<Dialog.Content
					className={cn(
						"fixed z-[100] flex flex-col w-full overflow-hidden bg-white shadow-xl duration-300 animate-in transition-all fade-in focus:outline-none max-sm:h-dvh",
					)}
					aria-label={tSearchBar("search")}
				>
					<VisuallyHidden>
						<Dialog.Title className="hidden">{tSearchBar("search")}</Dialog.Title>
					</VisuallyHidden>
					{isScrolled ? null : <div className="flex w-full lg:h-11" />}
					<div className="flex w-full h-dvh pt-[2px] lg:h-auto">
						<div className="flex flex-col mx-auto w-full max-w-container px-container-sm lg:px-container">
							<div className="relative flex flex-wrap items-center gap-xs gap-y-3xl py-lg lg:py-xl lg:flex-nowrap lg:gap-xs">
								<div className={cn("hidden shrink-0 transition-all lg:flex")}>
									{logo && (
										<Link href={{ pathname: "/" }} aria-label="homepage" className="block">
											<AssetImage
												unoptimized // svg
												data={logo}
												alt={siteName || logo.alt || ""}
												className="h-10 w-auto"
												width={140}
												height={40}
											/>
										</Link>
									)}
								</div>
								<div
									className={cn(
										"relative transition-all duration-300 ml-auto flex w-full lg:w-auto lg:max-w-[50%] lg:grow lg:px-12 lg:absolute lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:min-w-[640px]",
										animationClass,
									)}
								>
									<SearchBar />
								</div>
								<div className="hidden ml-auto lg:flex">
									<SearchClose />
								</div>
							</div>
							{searchQuery.trim().length > 1 ? (
								<div className="custom-scrollbar h-svh overflow-y-scroll overflow-x-hidden w-full pb-3xl lg:pt-4xl lg:h-auto">
									{results?.query && noResults && status !== "loading" ? (
										<div className="pt-lg flex flex-col gap-4xl">
											<div className="text-xl">
												{t.rich("title", {
													bold: (chunks) => <span className="font-bold">{chunks}</span>,
													search: query,
												})}
											</div>
											<div
												className="prose max-w-full"
												dangerouslySetInnerHTML={{ __html: t.raw("description") }}
											/>
										</div>
									) : (
										<>
											<div className="flex flex-col-reverse gap-xl lg:gap-7xl lg:flex-row">
												<Index indexName={indexName}>
													<Configure
														hitsPerPage={5}
														attributesToRetrieve={[
															"store",
															"slug",
															"objectID",
															"name",
															"address.addressLine",
															"address.streetNumber",
														]}
													/>
													<SearchStores />
												</Index>

												<Index indexName={`${country}_default`}>
													<Configure
														hitsPerPage={4}
														attributesToRetrieve={[
															"objectID",
															"slug",
															"name",
															"image",
															"neckline",
															"rawMaterial",
															"brand",
															"denier",
															"fit",
															"packSize",
															"startingPrice",
														]}
													/>

													<SearchProducts query={query} />
												</Index>
											</div>
										</>
									)}
								</div>
							) : null}
						</div>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
};

const SearchClose = () => {
	const t = useTranslations("Search");
	const { setSearchOpen } = useMenu();

	return (
		<Button
			onClick={() => {
				setSearchOpen(false);
			}}
			className={cn(
				"group items-center gap-md self-start rounded-none py-xxs font-semibold text-brand transition-all hover:text-brand-600 focus:bg-orange-dark-200 lg:inline-flex",
			)}
		>
			<span className="border-b border-brand leading-tight transition-all group-hover:border-transparent">
				{t("close")}
			</span>
			<Icon icon="x-close" className="size-5 bg-brand transition-all group-hover:bg-brand-600" />
		</Button>
	);
};
